<template>
  <transition name="library-delete-fade">
    <div
      v-if="visible"
      class="library-delete-backdrop"
      @click.self="$emit('cancel')"
    >
      <section
        class="library-delete-modal"
        role="alertdialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="messageId"
      >
        <header class="library-delete-header">
          <span class="library-delete-icon" aria-hidden="true">
            <i class="fa-solid fa-trash"></i>
          </span>
          <div class="library-delete-heading">
            <h2 :id="titleId">{{ title }}</h2>
            <p v-if="subtitle">{{ subtitle }}</p>
          </div>
          <button
            class="library-delete-close"
            type="button"
            title="Chiudi"
            aria-label="Chiudi la finestra"
            @click="$emit('cancel')"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>

        <div class="library-delete-body">
          <p :id="messageId" class="library-delete-message">
            {{ message }}
          </p>

          <div v-if="options.length" class="library-delete-options">
            <label
              v-for="option in options"
              :key="option.key"
              class="library-delete-option"
              :class="{ 'is-selected': selectedOptions[option.key] }"
            >
              <input
                type="checkbox"
                :checked="Boolean(selectedOptions[option.key])"
                @change="toggleOption(option.key, $event.target.checked)"
              />
              <span>{{ option.label }}</span>
            </label>
          </div>
        </div>

        <footer class="library-delete-actions">
          <button
            class="library-delete-button library-delete-button--cancel"
            type="button"
            @click="$emit('cancel')"
          >
            {{ cancelLabel }}
          </button>
          <button
            class="library-delete-button library-delete-button--danger"
            type="button"
            :disabled="confirmDisabled"
            @click="$emit('confirm')"
          >
            <i class="fa-solid fa-trash" aria-hidden="true"></i>
            {{ confirmLabel }}
          </button>
        </footer>
      </section>
    </div>
  </transition>
</template>

<script>
export default {
  name: "LibraryDeleteModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Elimina file dalla libreria",
    },
    subtitle: {
      type: String,
      default: "Eliminazione file",
    },
    message: {
      type: String,
      default: "",
    },
    options: {
      type: Array,
      default: () => [],
    },
    selectedOptions: {
      type: Object,
      default: () => ({}),
    },
    confirmLabel: {
      type: String,
      default: "Elimina",
    },
    cancelLabel: {
      type: String,
      default: "Annulla",
    },
    confirmDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const uid = this._uid || Math.random().toString(36).slice(2);
    return {
      titleId: `library-delete-title-${uid}`,
      messageId: `library-delete-message-${uid}`,
    };
  },
  watch: {
    visible(isVisible) {
      if (isVisible) {
        document.addEventListener("keydown", this.handleKeydown);
      } else {
        document.removeEventListener("keydown", this.handleKeydown);
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleKeydown);
  },
  methods: {
    toggleOption(key, checked) {
      this.$emit("toggle-option", { key, checked });
    },
    handleKeydown(event) {
      if (event.key === "Escape") this.$emit("cancel");
    },
  },
};
</script>

<style scoped>
.library-delete-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(17, 24, 31, 0.62);
  backdrop-filter: blur(1.5px);
}

.library-delete-modal {
  width: min(37rem, 100%);
  overflow: hidden;
  border: 1px solid #d7dce3;
  border-radius: 9px;
  background: #ffffff;
  box-shadow: 0 26px 68px rgba(4, 14, 24, 0.3);
  color: #132946;
  text-align: left;
}

.library-delete-header {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.95rem;
  padding: 1.35rem 1.45rem 1.15rem;
  border-bottom: 1px solid #dce2e9;
}

.library-delete-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.15rem;
  height: 3.15rem;
  border-radius: 50%;
  background: #fde7eb;
  color: #e62e46;
  font-size: 1.18rem;
}

.library-delete-heading {
  min-width: 0;
}

.library-delete-heading h2 {
  margin: 0;
  color: #122947;
  font-size: 1.32rem;
  font-weight: 750;
  line-height: 1.2;
}

.library-delete-heading p {
  margin: 0.28rem 0 0;
  color: #68778b;
  font-size: 0.76rem;
  font-weight: 750;
  letter-spacing: 0.035em;
  line-height: 1.2;
  text-transform: uppercase;
}

.library-delete-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #6b7b8f;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.18s ease, color 0.18s ease;
}

.library-delete-close:hover {
  background: #f0f3f6;
  color: #22364d;
}

.library-delete-body {
  padding: 1.25rem 1.45rem 1.35rem;
}

.library-delete-message {
  margin: 0;
  color: #233a56;
  font-size: 0.98rem;
  line-height: 1.48;
  white-space: pre-line;
}

.library-delete-options {
  margin-top: 1.05rem;
  overflow: hidden;
  border: 1px solid #d5dee8;
  border-radius: 8px;
  background: #ffffff;
}

.library-delete-option {
  display: flex;
  align-items: center;
  gap: 0.82rem;
  min-height: 3.1rem;
  padding: 0.65rem 1rem;
  border-bottom: 1px solid #dfe5ec;
  color: #263c57;
  font-size: 0.96rem;
  cursor: pointer;
  transition: background-color 0.16s ease;
}

.library-delete-option:last-child {
  border-bottom: 0;
}

.library-delete-option:hover {
  background: #f7faff;
}

.library-delete-option.is-selected {
  background: #eef6ff;
}

.library-delete-option input {
  width: 1.18rem;
  height: 1.18rem;
  margin: 0;
  accent-color: #667586;
  cursor: pointer;
}

.library-delete-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.85rem;
  padding: 1rem 1.45rem 1.2rem;
  border-top: 1px solid #e1e6ec;
  background: #ffffff;
}

.library-delete-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.48rem;
  min-width: 9rem;
  min-height: 2.65rem;
  padding: 0.65rem 1rem;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 0.92rem;
  font-weight: 750;
  cursor: pointer;
  transition: background-color 0.18s ease, border-color 0.18s ease,
    box-shadow 0.18s ease;
}

.library-delete-button--cancel {
  border-color: #dce1e7;
  background: #f0f2f5;
  color: #4d5968;
}

.library-delete-button--cancel:hover {
  background: #e7eaee;
}

.library-delete-button--danger {
  border-color: #ea263f;
  background: linear-gradient(135deg, #f32e48, #e91f3c);
  color: #ffffff;
  box-shadow: 0 7px 16px rgba(230, 36, 62, 0.16);
}

.library-delete-button--danger:hover:not(:disabled) {
  border-color: #d91e37;
  background: linear-gradient(135deg, #e92842, #d91e37);
}

.library-delete-button:disabled {
  opacity: 0.48;
  cursor: not-allowed;
  box-shadow: none;
}

.library-delete-fade-enter-active,
.library-delete-fade-leave-active {
  transition: opacity 0.18s ease;
}

.library-delete-fade-enter,
.library-delete-fade-leave-to {
  opacity: 0;
}

@media (max-width: 560px) {
  .library-delete-header,
  .library-delete-body,
  .library-delete-actions {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .library-delete-actions {
    flex-direction: column-reverse;
  }

  .library-delete-button {
    width: 100%;
  }
}
</style>
